import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { Driver, NetworkLocation, TourPreview, TourStopSelection, Vehicle } from '../types';

interface Props {
  stops: TourStopSelection[];
  vehicles: Vehicle[];
  drivers: Driver[];
  locations: NetworkLocation[];
  fillThreshold: number;
  onMove: (key: string, direction: -1 | 1) => void;
  /** Postavlja novi redosled stanica, ključevima. */
  onReorder: (keys: string[]) => void;
  onRemove: (key: string) => void;
  onSaved: () => void;
}

/** „8h 25min" — brojka koja se čita izdaleka, bez reči oko sebe. */
function formatShort(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);

  return hours > 0 ? `${hours}h ${rest}min` : `${rest} min`;
}

/** Šifra odredišta iz oznake tipa „MPO 003". */
function codeOf(destination: string): string | null {
  const match = destination.match(/(\d{3})\s*$/);
  return match ? match[1] : null;
}

/** Kako se stanica zove na spisku ture. */
function labelOf(stop: TourStopSelection): string {
  switch (stop.kind) {
    case 'mpo':
      return stop.group.destination;
    case 'preuzimanje':
      return stop.stop.label;
    default:
      return `${stop.delivery.address}, ${stop.delivery.city}`;
  }
}

function pieceCountOf(stop: TourStopSelection): number {
  switch (stop.kind) {
    case 'mpo':
      return stop.group.totalQuantity;
    case 'preuzimanje':
      return stop.stop.pieces;
    default:
      return stop.delivery.pieces;
  }
}

/**
 * Slaganje ture od izabranih stanica.
 *
 * Popunjenost i vreme se računaju na zbir svih stanica, ne po jednom mestu.
 * Upozorenja ne blokiraju — operater sme svesno da nastavi, ali za nisku
 * popunjenost mora uneti razlog.
 */
export default function TourBuilder({
  stops, vehicles, drivers, locations, fillThreshold, onMove, onReorder, onRemove, onSaved
}: Props) {
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [driverId, setDriverId] = useState<number | null>(null);
  const [baseCode, setBaseCode] = useState('021');
  const [name, setName] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [preview, setPreview] = useState<TourPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  /** Šta je poslednji predlog redosleda uštedeo, da operater vidi da je vredelo. */
  const [ordered, setOrdered] = useState<string | null>(null);
  const [ordering, setOrdering] = useState(false);

  const warehouses = useMemo(
    () => locations.filter((l) => l.kind === 'Warehouse' && l.canBeRouted),
    [locations]
  );

  useEffect(() => {
    if (vehicleId === null && vehicles.length > 0) setVehicleId(vehicles[0].id);
  }, [vehicles, vehicleId]);

  useEffect(() => {
    const available = drivers.find((d) => d.isAvailable);
    if (driverId === null && available) setDriverId(available.id);
  }, [drivers, driverId]);

  // Uz nacrt idu i ključevi stanica, istim redom i sa istim izostavljanjima.
  // Bez toga se predloženi redosled ne bi mogao vratiti na spisak: indeksi bi
  // pokazivali na stanice koje u nacrt nisu ni ušle.
  const { draft, draftKeys } = useMemo(() => {
    if (vehicleId === null || driverId === null) return { draft: null, draftKeys: [] as string[] };

    const keys: string[] = [];

    const draftStops = stops
      .map((stop) => {
        if (stop.kind === 'kupac') {
          keys.push(stop.key);
          // Usluge se ne šalju — one ne zauzimaju mesto u vozilu, pa bi samo
          // pokvarile procenu popunjenosti.
          return {
            kind: 1, // CustomerAddress
            customerAddressId: stop.delivery.addressId,
            items: stop.delivery.items
              .filter((i) => !i.isService)
              .map((i) => ({
                articleCode: i.articleCode,
                articleName: i.articleName,
                quantity: i.quantity,
                erpDocument: stop.delivery.documentKey
              }))
          };
        }

        if (stop.kind === 'preuzimanje') {
          if (!stop.stop.locationCode) return null;

          keys.push(stop.key);

          // Ista roba stoji i na kupčevoj stanici. Ovde se šalje da vozač zna
          // šta podiže; popunjenost i redosled utovara je namerno preskaču.
          return {
            kind: 0, // NetworkLocation
            locationKind: stop.stop.locationKind === 'Warehouse' ? 1 : 0,
            locationCode: stop.stop.locationCode,
            isPickup: true,
            items: stop.stop.items.map((i) => ({
              articleCode: i.articleCode,
              articleName: i.articleName,
              quantity: i.quantity,
              erpDocument: i.erpDocument
            }))
          };
        }

        const code = codeOf(stop.group.destination);
        if (!code) return null;

        keys.push(stop.key);

        return {
          kind: 0, // NetworkLocation
          locationKind: 0, // Store
          locationCode: code,
          // Nalog ide uz stavku: po njemu magacioner zna šta pakuje, a kasnije
          // se zna i iz kog dokumenta je roba otišla.
          items: stop.group.items.map((i) => ({
            articleCode: i.articleCode,
            articleName: i.articleName,
            quantity: i.quantity,
            erpDocument: i.sourceDocument
          }))
        };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    if (draftStops.length === 0) return { draft: null, draftKeys: [] as string[] };

    const built = {
      name: name.trim() || `Tura ${new Date().toLocaleDateString('sr-RS')}`,
      date: new Date().toISOString().slice(0, 10),
      vehicleId,
      driverId,
      startKind: 1, // Warehouse
      startCode: baseCode,
      endKind: 1,
      endCode: baseCode,
      stops: draftStops,
      lowFillOverrideReason: overrideReason.trim() || null
    };

    return { draft: built, draftKeys: keys };
  }, [stops, vehicleId, driverId, baseCode, name, overrideReason]);

  const refresh = useCallback(async () => {
    if (!draft) {
      setPreview(null);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      setPreview(await api.previewTour(draft));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }, [draft]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const lowFill = preview?.fill.canBeComputed === true
    && preview.fill.fillPercent < fillThreshold
    && !preview.fill.isOverloaded;
  const needsReason = lowFill && overrideReason.trim().length === 0;

  /**
   * Traži bolji redosled za stanice koje su već izabrane.
   *
   * Ne dodaje i ne izbacuje nijednu — samo ih poređa. Operater posle toga i
   * dalje pomera šta hoće strelicama; ovo je predlog, ne naredba.
   */
  const reorder = async () => {
    if (!draft) return;

    setOrdering(true);
    try {
      const result = await api.suggestOrder(draft);

      if (!result.changed || !result.order) {
        setOrdered('Redosled je već dobar — nema šta da se popravi.');
        return;
      }

      onReorder(result.order.map((index) => draftKeys[index]));

      setOrdered(
        `Složeno: ${result.beforeKm} → ${result.afterKm} km`
        + ` (${result.savedKm} km kraće)`
        + (result.measured ? '' : ', po vazdušnoj liniji — rutiranje nije odgovorilo'));

      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOrdering(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.saveTour(draft);
      setSaved(`Tura „${result.name}" sačuvana — ${result.stops} stanica. Roba je rezervisana.`);
      setOverrideReason('');
      setName('');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Boja brojke popunjenosti: preko kapaciteta, ispod praga, ili u redu. */
  const fillTone = preview?.fill.isOverloaded
    ? 'figure--bad'
    : lowFill ? 'figure--warn' : preview?.fill.canBeComputed ? 'figure--ok' : '';

  if (stops.length === 0) {
    return (
      <p className="empty">
        Izaberi odredišta i isporuke sa leve strane — mogu se mešati u istoj turi.
        Redosled im daješ ovde.
      </p>
    );
  }

  return (
    <div className="builder">
      {/* Traka odluke. Popunjenost, trajanje i „Sačuvaj" stoje NA VRHU i lepe se
          uz njega dok se skroluje, jer se po ta dva broja odlučuje na svaki
          klik po levim listama. Ranije su bili na dnu kolone od četrnaest
          elemenata, ispod četiri padajuće liste koje se postave jednom. */}
      <div className="decide">
        <div className="decide__figures">
          <span className={`figure ${fillTone}`}>
            <span className="figure__label">Popunjenost</span>
            <span className="figure__value">
              {preview?.fill.canBeComputed ? `${preview.fill.fillPercent}%` : '—'}
            </span>
            <span className="figure__sub">
              {preview?.fill.canBeComputed
                ? `${preview.fill.loadedVolumeM3} od ${preview.fill.vehicleCapacityM3} m³`
                : 'zapremina nije poznata'}
            </span>
          </span>

          <span className={`figure ${preview && !preview.timing.fitsInShift ? 'figure--warn' : ''}`}>
            <span className="figure__label">Trajanje</span>
            <span className="figure__value">{preview ? formatShort(preview.timing.totalMinutes) : '—'}</span>
            <span className="figure__sub">
              {preview
                ? (preview.timing.fitsInShift
                    ? `staje u smenu · ${preview.timing.distanceKm} km`
                    : `preko smene · ${preview.timing.distanceKm} km`)
                : 'računa se'}
            </span>
          </span>
        </div>

        <div className="decide__act">
          <button
            type="button"
            className="primary"
            onClick={() => void save()}
            disabled={busy || !draft || preview?.canBeSaved === false || needsReason}
          >
            {busy ? 'Čuvam…' : 'Sačuvaj turu'}
          </button>
          {busy && <span className="muted">Računam…</span>}
        </div>
      </div>

      {error && <p className="warning">{error}</p>}
      {saved && <div className="health health--ok">{saved}</div>}

      {preview && !busy && (
        <>
          {preview.blockers.map((b) => (
            <p key={b} className="blocker">{b}</p>
          ))}
          {preview.warnings.map((w) => (
            <p key={w} className="warning">{w}</p>
          ))}
        </>
      )}

      {needsReason && (
        <div className="field">
          <label htmlFor="reason">Razlog za puštanje ispod praga</label>
          <input
            id="reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="npr. Odžaci čekaju tri dana"
          />
          <p className="note">Razlog se beleži uz turu, da se kasnije vidi zašto je puštena.</p>
        </div>
      )}

      <ol className="stops">
        {stops.map((stop, index) => (
          <li
            key={stop.key}
            className={`stop${stop.kind === 'kupac' ? ' stop--kupac' : ''}`
              + `${stop.kind === 'preuzimanje' ? ' stop--preuzimanje' : ''}`}
          >
            <span className="stop__index">{index + 1}</span>
            <span className="stop__name">
              {labelOf(stop)}
              <span className="stop__meta">
                {stop.kind === 'kupac' && (
                  <>
                    kupac · {pieceCountOf(stop)} kom
                    {stop.delivery.requiresTwoPeople && <strong> · dva čoveka</strong>}
                  </>
                )}
                {stop.kind === 'mpo' && (
                  <>{stop.group.items.length} stavki · {pieceCountOf(stop)} kom</>
                )}
                {stop.kind === 'preuzimanje' && (
                  <><strong>uzima se</strong> · {pieceCountOf(stop)} kom za{' '}
                  {stop.stop.pickupFor.length} isporuk
                  {stop.stop.pickupFor.length === 1 ? 'u' : 'e'}</>
                )}
              </span>
            </span>
            <span className="stop__actions">
              <button type="button" onClick={() => onMove(stop.key, -1)} disabled={index === 0} title="Pomeri gore">↑</button>
              <button type="button" onClick={() => onMove(stop.key, 1)} disabled={index === stops.length - 1} title="Pomeri dole">↓</button>
              <button type="button" onClick={() => onRemove(stop.key)} title="Izbaci iz ture">×</button>
            </span>
          </li>
        ))}
      </ol>

      {/* Slaganje stoji uz spisak jer nad njim i radi. */}
      {stops.length > 2 && (
        <div className="builder__order">
          <button type="button" disabled={ordering || !draft} onClick={() => void reorder()}>
            {ordering ? 'Slažem…' : 'Složi redosled'}
          </button>
          {ordered && <span className="muted">{ordered}</span>}
        </div>
      )}

      {/* Podešavanja se postave jednom po turi, pa stoje sklopljena. Otvaraju se
          sama dok vozilo ili vozač nisu izabrani — tada su posao, ne podatak. */}
      <details className="details" open={vehicleId === null || driverId === null}>
        <summary>Podešavanja ture</summary>

        <div className="field">
          <label htmlFor="name">Naziv ture</label>
          <input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="npr. Zapadna tura" />
        </div>

        <div className="field">
          <label htmlFor="base">Polazište i povratak</label>
          <select id="base" value={baseCode} onChange={(e) => setBaseCode(e.target.value)}>
            {warehouses.map((w) => (
              <option key={w.code} value={w.code}>{w.name}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="vehicle">Vozilo</label>
          <select id="vehicle" value={vehicleId ?? ''} onChange={(e) => setVehicleId(Number(e.target.value))}>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.registration} — {v.capacityM3.toFixed(2)} m³{!v.capacityIsMeasured ? ' (privremeno)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="driver">Vozač</label>
          <select id="driver" value={driverId ?? ''} onChange={(e) => setDriverId(Number(e.target.value))}>
            {drivers.map((d) => (
              <option key={d.id} value={d.id} disabled={!d.isAvailable}>
                {d.fullName}{d.isAvailable ? ` — ${d.shiftStart}–${d.shiftEnd}` : ' — ne radi'}
              </option>
            ))}
          </select>
        </div>
      </details>

      {preview && !busy && (
        <>
          {/* Rečenice ostaju — one objašnjavaju broj iznad. Broj se skenira,
              rečenica se čita kad nešto ne valja. */}
          <div className="estimate">
            <div className={`gauge ${preview.fill.isOverloaded ? 'gauge--over' : lowFill ? 'gauge--low' : 'gauge--ok'}`}>
              <div className="gauge__fill" style={{ width: `${Math.min(100, preview.fill.fillPercent)}%` }} />
            </div>
            <p className="explanation">{preview.fill.explanation}</p>
            <p className={`explanation ${preview.timing.fitsInShift ? '' : 'explanation--warn'}`}>
              {preview.timing.explanation}
            </p>
          </div>

          {preview.timing.legs.length > 0 && (
            <details className="details">
              <summary>Deonice — {preview.timing.distanceKm} km ukupno</summary>
              <ul className="legs">
                {preview.timing.legs.map((leg, i) => (
                  <li key={i}>
                    {leg.from} → {leg.to}
                    <span className="muted"> {leg.distanceKm} km · {leg.driveMinutes} min</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {preview.loadingOrder.length > 0 && (
            <details className="details" open>
              <summary>Redosled utovara</summary>
              <ol className="legs">
                {preview.loadingOrder.map((p) => (
                  <li key={p.loadingSequence}>
                    {p.description}
                    {p.isNearestDoor && <strong> — do vrata, prvo se isporučuje</strong>}
                  </li>
                ))}
              </ol>
            </details>
          )}

        </>
      )}
    </div>
  );
}
